import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import GuestForgotPasswordModal from './GuestForgotPasswordModal';

const { FiHome, FiLock, FiUser, FiInfo } = FiIcons;

const AccessForm = ({ guestPage, property, onAuthenticate }) => {
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Check if this is the demo page
  const isDemo = guestPage?.slug === 'demo-guest';

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate authentication delay
    setTimeout(() => {
      // Check against the stored lastFourDigits (which is now the general Access Password)
      if (accessCode === guestPage.lastFourDigits) {
        onAuthenticate(true);
        toast.success('Access granted! Welcome to your portal.');
      } else {
        toast.error('Invalid access password. Please try again.');
        setLoading(false);
      }
    }, 1000);
  };

  const fillDemoCode = () => {
    setAccessCode('1234');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{
        backgroundImage: property?.backgroundImage 
          ? `url(${property.backgroundImage})` 
          : 'linear-gradient(to bottom right, rgb(239, 246, 255), rgb(224, 231, 255))',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay for better readability */}
      <div className="absolute inset-0 bg-black/20"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 relative z-10"
      >
        <div className="text-center mb-8">
          {property?.image && !imageError ? (
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full overflow-hidden border-4 border-blue-200 shadow-lg mb-4">
              <img 
                src={property.image} 
                alt={property.name}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div className="inline-flex items-center justify-center w-24 h-24 bg-blue-600 rounded-full mb-4">
              <SafeIcon icon={FiHome} className="text-white text-3xl" />
            </div>
          )}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome!</h1>
          <p className="text-gray-600">
            Access your guest portal for <br />
            <strong>{property?.name}</strong>
          </p>
        </div>

        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiUser} className="text-blue-600" />
            <div>
              <p className="font-medium text-gray-900">{guestPage?.guestName}</p>
              <p className="text-sm text-gray-600">
                Reservation: {guestPage?.reservationId || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Access Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiLock} className="text-gray-400" />
              </div>
              <input
                type="text" // Changed from pattern/maxLength restriction to allow generic passwords
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono"
                placeholder="Enter access password"
                required
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500 text-center flex-1">
                Please enter the access password provided in your email (usually last 4 digits of phone).
              </p>
            </div>
            <div className="text-center mt-2">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Forgot access code?
              </button>
            </div>
          </div>

          {isDemo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-green-50 border border-green-100 rounded-lg p-3 text-sm text-green-800 flex items-center justify-between"
            >
              <div className="flex items-center">
                <SafeIcon icon={FiInfo} className="mr-2" />
                <span>Demo Code: <strong>1234</strong></span>
              </div>
              <button
                type="button"
                onClick={fillDemoCode}
                className="text-xs bg-green-200 hover:bg-green-300 px-2 py-1 rounded text-green-800 font-medium transition-colors"
              >
                Auto-fill
              </button>
            </motion.div>
          )}

          <motion.button
            type="submit"
            disabled={loading || !accessCode}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Access Portal'}
          </motion.button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Need help? Contact property support for assistance.
          </p>
        </div>
      </motion.div>

      {showForgotPassword && (
        <GuestForgotPasswordModal
          onClose={() => setShowForgotPassword(false)}
          slug={guestPage?.slug}
        />
      )}
    </div>
  );
};

export default AccessForm;